import { AppLayoutPanelProps } from '@src/components/sco/AppLayout/Panel'
import { createContext, FC, ReactNode, useContext, useMemo, useState } from 'react'

/**
 * Interactive flags that survive soft navigations.
 * Everything else on a panel — especially `children` — always comes from the latest
 * `props.panels` so client-side route changes actually swap the main panel page.
 *
 * Prior implementation stored full panel objects (including children) in useState and
 * synced via useEffect. Soft nav + page `updatePanel()` races re-applied stale children
 * so the URL changed while edu-main kept rendering the previous page.
 */
type PanelUiState = {
  isPanelOpen?: boolean
  isDrawerOpen?: boolean
  isUsingDrawer?: boolean
}

export type AppLayoutContextValue = {
  id?: string
  parentLayout?: AppLayoutContextValue
  addPanel?: (panel: AppLayoutPanelProps) => void
  closeDrawer?: (id: string) => void
  closePanel?: (id: string) => void
  getPanel?: (id: string) => AppLayoutPanelProps | undefined
  findPanel?: (id: string) => AppLayoutPanelProps | undefined
  getPanelAtIndex?: (index: number) => AppLayoutPanelProps | undefined
  isPanelOpen?: (id: string) => boolean
  isPanelClosed?: (id: string) => boolean
  isPanelUsingDrawer?: (id: string) => boolean
  openDrawer?: (id: string) => void
  openPanel?: (id: string) => void
  panels?: AppLayoutPanelProps[]
  removePanel?: (id: string) => void
  setPanels?: (panels: AppLayoutPanelProps[]) => void
  toggleDrawer?: (id: string) => void
  togglePanel?: (id: string) => void
  toggle?: (id: string) => void
  updatePanel?: (id: string, changes: AppLayoutPanelProps) => void
}

export const AppLayoutContext = createContext<AppLayoutContextValue>({})

export const useParentAppLayout = () => useContext(AppLayoutContext)

const pickUiState = (panel: AppLayoutPanelProps | PanelUiState): PanelUiState => ({
  ...(panel.isPanelOpen !== undefined ? { isPanelOpen: panel.isPanelOpen } : {}),
  ...(panel.isDrawerOpen !== undefined ? { isDrawerOpen: panel.isDrawerOpen } : {}),
  ...(panel.isUsingDrawer !== undefined ? { isUsingDrawer: panel.isUsingDrawer } : {}),
})

const applyUiState = (panel: AppLayoutPanelProps, ui?: PanelUiState): AppLayoutPanelProps => {
  if (!ui) {
    return panel
  }
  return {
    ...panel,
    isPanelOpen: ui.isPanelOpen ?? panel.isPanelOpen,
    isDrawerOpen: ui.isDrawerOpen ?? panel.isDrawerOpen,
    isUsingDrawer: ui.isUsingDrawer ?? panel.isUsingDrawer,
  }
}

export const AppLayoutProvider: FC<{
  id?: string
  panels?: AppLayoutPanelProps[]
  children?: ReactNode[] | ReactNode
}> = props => {
  const parentLayout = useParentAppLayout()
  const [layoutId] = useState(props.id || 'app-layout')
  // Open/drawer overrides only — never store page `children` here.
  const [uiById, setUiById] = useState<Record<string, PanelUiState>>({})
  // Runtime-only panels (API completeness; education/events use props.panels).
  const [dynamicPanels, setDynamicPanels] = useState<AppLayoutPanelProps[]>([])

  const panels = useMemo(() => {
    const fromProps = props.panels || []
    const propIds = new Set(fromProps.map(p => p.id).filter(Boolean) as string[])

    const mergedProps = fromProps.map(panel => applyUiState(panel, panel.id ? uiById[panel.id] : undefined))

    const extras = dynamicPanels
      .filter(p => p.id && !propIds.has(p.id))
      .map(panel => applyUiState(panel, panel.id ? uiById[panel.id] : undefined))

    return extras.length ? [...mergedProps, ...extras] : mergedProps
  }, [props.panels, uiById, dynamicPanels])

  const patchUi = (id: string, changes: AppLayoutPanelProps | PanelUiState) => {
    const uiPatch = pickUiState(changes)
    if (!Object.keys(uiPatch).length) {
      return
    }
    setUiById(prev => ({
      ...prev,
      [id]: { ...prev[id], ...uiPatch },
    }))
  }

  const isOwnedHere = (id: string) =>
    (props.panels || []).some(p => p.id === id) || dynamicPanels.some(p => p.id === id) || id in uiById

  const addPanel = (panel: AppLayoutPanelProps) => {
    setDynamicPanels(prev => [...prev, panel])
    if (panel.id) {
      patchUi(panel.id, panel)
    }
  }

  const removePanel = (id: string) => {
    if (!isOwnedHere(id)) {
      parentLayout.removePanel?.(id)
      return
    }
    setDynamicPanels(prev => prev.filter(p => p.id !== id))
    setUiById(prev => {
      if (!(id in prev)) {
        return prev
      }
      const next = { ...prev }
      delete next[id]
      return next
    })
  }

  const updatePanel = (id: string, changes: AppLayoutPanelProps) => {
    if (!isOwnedHere(id)) {
      parentLayout.updatePanel?.(id, changes)
      return
    }

    patchUi(id, changes)

    if (dynamicPanels.some(p => p.id === id)) {
      setDynamicPanels(prev => prev.map(p => (p.id === id ? { ...p, ...changes } : p)))
    }
  }

  const findPanel = (id: string = ''): AppLayoutPanelProps => {
    return (
      panels.find(p => p.id === id) ||
      (id.includes('*') ? panels.find(p => p.id?.endsWith(id.replace('*', ''))) : null) ||
      parentLayout.getPanel?.(id)
    )
  }

  const setPanels = (next: AppLayoutPanelProps[]) => {
    setDynamicPanels(next)
    setUiById(prev => {
      const merged = { ...prev }
      for (const panel of next) {
        if (!panel.id) continue
        const ui = pickUiState(panel)
        if (Object.keys(ui).length) {
          merged[panel.id] = { ...merged[panel.id], ...ui }
        }
      }
      return merged
    })
  }

  const getPanelAtIndex = (index: number) => panels[index]

  const getPanel = (id: string) => findPanel(id)

  const openPanel = (id: string) => {
    if (!findPanel(id)) {
      parentLayout.openPanel?.(id)
      return
    }
    updatePanel(id, { isPanelOpen: true })
  }

  const closePanel = (id: string) => {
    if (!findPanel(id)) {
      parentLayout.closePanel?.(id)
      return
    }
    updatePanel(id, { isPanelOpen: false })
  }

  const toggle = (id: string) => {
    const panel = findPanel(id)
    if (!panel) {
      parentLayout.toggle?.(id)
      return
    }
    updatePanel(id, {
      isPanelOpen: panel.isPanelOpen === true ? false : true,
      isDrawerOpen: panel.isDrawerOpen === true ? false : true,
    })
  }

  const togglePanel = (id: string) => {
    const panel = findPanel(id)
    if (!panel) {
      parentLayout.togglePanel?.(id)
      return
    }

    setUiById(prev => {
      const next: Record<string, PanelUiState> = { ...prev }
      for (const p of panels) {
        if (!p.id) continue
        if (p.id !== panel.id) {
          next[p.id] = { ...next[p.id], isDrawerOpen: false }
        } else {
          const currentOpen = next[p.id]?.isPanelOpen ?? p.isPanelOpen
          const currentDrawer = next[p.id]?.isDrawerOpen ?? p.isDrawerOpen
          next[p.id] = {
            ...next[p.id],
            isPanelOpen: currentOpen !== false ? false : true,
            isDrawerOpen: currentDrawer === true ? false : true,
          }
        }
      }
      return next
    })
  }

  const openDrawer = (id: string) => {
    if (!findPanel(id)) {
      parentLayout.openDrawer?.(id)
      return
    }
    updatePanel(id, { isDrawerOpen: true })
  }

  const closeDrawer = (id: string) => {
    if (!findPanel(id)) {
      parentLayout.closeDrawer?.(id)
      return
    }
    updatePanel(id, { isDrawerOpen: false })
  }

  const toggleDrawer = (id: string) => {
    const panel = findPanel(id)
    if (!panel) {
      parentLayout.toggleDrawer?.(id)
      return
    }
    updatePanel(id, { isDrawerOpen: panel.isDrawerOpen === true ? false : true })
  }

  const isPanelOpen = (id: string): boolean => {
    const panel = findPanel(id)
    if (!panel) {
      return parentLayout.isPanelOpen?.(id) ?? false
    }
    return panel.isUsingDrawer ? !!panel.isDrawerOpen : !!panel.isPanelOpen
  }

  const isPanelClosed = (id: string): boolean => {
    const panel = findPanel(id)
    if (!panel) {
      return parentLayout.isPanelClosed?.(id) ?? false
    }
    return panel.isUsingDrawer ? !panel.isDrawerOpen : !panel.isPanelOpen
  }

  const isPanelUsingDrawer = (id: string): boolean => {
    const panel = findPanel(id)
    if (!panel) {
      return parentLayout.isPanelUsingDrawer?.(id) ?? false
    }
    return !!panel.isUsingDrawer
  }

  return (
    <AppLayoutContext.Provider
      value={{
        id: layoutId,
        parentLayout,
        addPanel,
        closeDrawer,
        closePanel,
        getPanel,
        getPanelAtIndex,
        isPanelOpen,
        isPanelClosed,
        isPanelUsingDrawer,
        findPanel,
        openDrawer,
        openPanel,
        panels,
        removePanel,
        setPanels,
        toggle,
        toggleDrawer,
        togglePanel,
        updatePanel,
      }}
    >
      {props.children}
    </AppLayoutContext.Provider>
  )
}
